// Enhanced Split Bill Details View with Advanced Features
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Paper,
  Stack,
  LinearProgress,
  Fade,
  Zoom,
  useTheme,
  alpha,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Skeleton,
  Alert,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Menu,
  Snackbar
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  QrCode as QrCodeIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Message as MessageIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  History as HistoryIcon,
  Notifications as NotificationIcon,
  Link as LinkIcon,
  AttachMoney as MoneyIcon,
  Calculate as CalculateIcon,
  PersonAdd as PersonAddIcon,
  ContentCopy as CopyIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as WalletIcon,
  Restaurant as RestaurantIcon,
  LocalGroceryStore as GroceryIcon,
  DirectionsCar as TravelIcon,
  Home as HomeIcon,
  Theaters as MovieIcon,
  SportsEsports as EntertainmentIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/helpers';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { generateSettlementPDF, getReportWebUrl } from './SettlementReport';

const EnhancedSplitBillDetails = () => {
  const { currency } = useCurrency(); // eslint-disable-line no-unused-vars
  const { billId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();

  // State management
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [shareDialog, setShareDialog] = useState(false);
  const [addPersonDialog, setAddPersonDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [reminderDialog, setReminderDialog] = useState(false);
  const [qrCodeDialog, setQrCodeDialog] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [reportMenuAnchor, setReportMenuAnchor] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [reminderMessage, setReminderMessage] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [refreshing, setRefreshing] = useState(false);

  // Category icons mapping
  const getCategoryIcon = (category) => {
    const iconMap = {
      restaurant: <RestaurantIcon />,
      grocery: <GroceryIcon />,
      travel: <TravelIcon />,
      home: <HomeIcon />,
      entertainment: <EntertainmentIcon />,
      movie: <MovieIcon />,
      default: <ReceiptIcon />
    };
    return iconMap[category] || iconMap.default;
  };

  // Get category color
  const getCategoryColor = (category) => {
    const colorMap = {
      restaurant: '#ff6b6b',
      grocery: '#4ecdc4',
      travel: '#45b7d1',
      home: '#96ceb4',
      entertainment: '#ffeaa7',
      movie: '#dda0dd',
      default: '#95a5a6'
    };
    return colorMap[category] || colorMap.default;
  };

  // Calculate participant details
  const participantDetails = useMemo(() => {
    if (!bill || !bill.participants) return [];

    return bill.participants.map(participant => {
      const balance = participant.paidAmount - participant.share;
      return {
        ...participant,
        balance,
        owes: balance < 0 ? Math.abs(balance) : 0,
        owed: balance > 0 ? balance : 0,
        isCurrentUser: participant.user === user.id || participant.email === user.email
      };
    });
  }, [bill, user]);

  // Calculate totals
  const totals = useMemo(() => {
    if (!bill) return { totalOwed: 0, totalOwing: 0, totalPaid: 0 };

    const currentUserParticipant = participantDetails.find(p => p.isCurrentUser);
    
    return {
      totalOwed: participantDetails.reduce((sum, p) => sum + p.owed, 0),
      totalOwing: participantDetails.reduce((sum, p) => sum + p.owes, 0),
      totalPaid: participantDetails.reduce((sum, p) => sum + (p.paidAmount || 0), 0),
      userOwes: currentUserParticipant?.owes || 0,
      userOwed: currentUserParticipant?.owed || 0,
      userPaid: currentUserParticipant?.hasPaid || false
    };
  }, [participantDetails]);

  // Fetch bill details
  const fetchBillDetails = async () => {
    try {
      if (!refreshing) setLoading(true);
      const response = await fetch(`https://soldikeeper-backend-production.up.railway.app/api/split-bills/${billId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bill details');
      }

      const data = await response.json();
      setBill(data.splitBill || data);
      setError(null);
    } catch (err) {
      console.error('Error fetching bill details:', err);
      setError(err.message || 'Failed to load bill details');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Handle payment
  const handlePayment = async () => {
    try {
      const amount = parseFloat(paymentAmount);
      if (!amount || amount <= 0) {
        setError('Please enter a valid amount');
        return;
      }

      const response = await fetch(`https://soldikeeper-backend-production.up.railway.app/api/split-bills/${billId}/pay`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          amount,
          method: paymentMethod,
          participantId: selectedParticipant?.id
        })
      });

      if (!response.ok) {
        throw new Error('Payment failed');
      }

      setSnackbar({ open: true, message: 'Payment recorded successfully!', severity: 'success' });
      setPaymentDialog(false);
      setPaymentAmount('');
      fetchBillDetails();
    } catch (err) {
      console.error('Payment error:', err);
      setError('Payment failed. Please try again.');
    }
  };

  // Handle reminder
  const handleSendReminder = async () => {
    try {
      const response = await fetch(`https://soldikeeper-backend-production.up.railway.app/api/split-bills/${billId}/remind`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          participantId: selectedParticipant?.id,
          message: reminderMessage
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send reminder');
      }

      setSnackbar({ open: true, message: 'Reminder sent successfully!', severity: 'success' });
      setReminderDialog(false);
      setReminderMessage('');
    } catch (err) {
      console.error('Reminder error:', err);
      setError('Failed to send reminder');
    }
  };

  // Handle share
  const handleShare = async (platform) => {
    const shareUrl = `${window.location.origin}/split-bills/join/${bill._id}`;
    const shareText = `Check out this split bill: ${bill.title} - Total: ${formatCurrency(bill.totalAmount)}`;

    switch (platform) {
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        setSnackbar({ open: true, message: 'Link copied to clipboard!', severity: 'success' });
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`);
        break;
      case 'telegram':
        window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`);
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(bill.title)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
        break;
      default:
        break;
    }
    setShareDialog(false);
  };

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      fetchBillDetails();
    }, 800);
  };

  const handleDownloadReportPDF = () => {
    if (!bill) return;
    try {
      generateSettlementPDF(bill);
      setSnackbar({ open: true, message: 'Settlement report downloaded!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Could not generate PDF. Try again.', severity: 'error' });
    }
    setReportMenuAnchor(null);
  };

  const handleCopyReportLink = async () => {
    if (!bill) return;
    let b = bill;
    if (!b.shareCode) {
      // Auto-generate share code if missing
      try {
        const { SplitSmartAPI } = await import('../../services/splitSmartAPI');
        const res = await SplitSmartAPI.generateShareCode ? SplitSmartAPI.generateShareCode(b._id) : null;
        if (res && res.data) b = { ...b, shareCode: res.data.shareCode || res.data };
      } catch {}
    }
    const url = getReportWebUrl(b);
    if (!url) {
      setSnackbar({ open: true, message: 'Share code not yet generated for this bill.', severity: 'warning' });
      setReportMenuAnchor(null);
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setSnackbar({ open: true, message: 'Report link copied to clipboard!', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: `Share this URL:\n${url}`, severity: 'info' });
    }
    setReportMenuAnchor(null);
  };

  useEffect(() => {
    fetchBillDetails();
  }, [billId]);

  if (loading && !refreshing) {
    return (
      <Box sx={{ maxWidth: '90%', mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3, mb: 3 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3, mb: 2 }} />
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error && !bill) {
    return (
      <Box sx={{ maxWidth: '90%', mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: 3, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/dashboard/split-bills')}>
          Back to Split Bills
        </Button>
      </Box>
    );
  }

  if (!bill) return null;

  return (
    <Fade in timeout={800}>
      <Box sx={{ maxWidth: '90%', mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/dashboard/split-bills')} sx={{ color: 'text.secondary' }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="600">
              {bill.title}
            </Typography>
            <Chip
              label={bill.status}
              color={bill.status === 'completed' ? 'success' : 'primary'}
              size="small"
            />
          </Box>
          
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={handleRefresh} disabled={refreshing}>
                {refreshing ? (
                  <RefreshIcon sx={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVertIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Main Bill Info */}
        <Paper
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${getCategoryColor(bill.category)}, ${alpha(getCategoryColor(bill.category), 0.8)})`,
            borderRadius: { xs: 2, sm: 3 },
            p: { xs: 2, sm: 3, md: 4 },
            mb: { xs: 2, sm: 3, md: 4 },
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.common.white, 0.2),
                    color: 'white',
                    width: { xs: 60, sm: 80 },
                    height: { xs: 60, sm: 80 },
                  }}
                >
                  {getCategoryIcon(bill.category)}
                </Avatar>
              </Grid>
              
              <Grid item xs>
                <Typography variant={{ xs: "h6", sm: "h5" }} fontWeight="700" gutterBottom>
                  {formatCurrency(bill.totalAmount)}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mb: 1 }}>
                  {bill.description || 'No description'}
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Chip
                    icon={<GroupIcon />}
                    label={`${participantDetails.length} participants`}
                    variant="outlined"
                    size="small"
                    sx={{ color: 'white', borderColor: alpha(theme.palette.common.white, 0.5) }}
                  />
                  <Chip
                    icon={<ScheduleIcon />}
                    label={new Date(bill.date).toLocaleDateString()}
                    variant="outlined"
                    size="small"
                    sx={{ color: 'white', borderColor: alpha(theme.palette.common.white, 0.5) }}
                  />
                  <Chip
                    label={bill.splitMethod}
                    variant="outlined"
                    size="small"
                    sx={{ color: 'white', borderColor: alpha(theme.palette.common.white, 0.5) }}
                  />
                </Stack>
              </Grid>

              {/* User's Status */}
              <Grid item>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.common.white, 0.1),
                    backdropFilter: 'blur(8px)',
                    borderRadius: 2,
                    textAlign: 'center',
                    border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>
                    Your Status
                  </Typography>
                  {totals.userOwes > 0 ? (
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                      You owe {formatCurrency(totals.userOwes)}
                    </Typography>
                  ) : totals.userOwed > 0 ? (
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                      You're owed {formatCurrency(totals.userOwed)}
                    </Typography>
                  ) : (
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                      All settled up
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Content Tabs */}
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': {
                borderRadius: 2,
                mr: 1,
                minHeight: 48,
                fontWeight: 600
              }
            }}
          >
            <Tab icon={<GroupIcon />} label="Participants" />
            <Tab icon={<ReceiptIcon />} label="Expenses" />
            <Tab icon={<HistoryIcon />} label="Activity" />
            <Tab icon={<CalculateIcon />} label="Summary" />
          </Tabs>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            {/* Tab Content */}
            {activeTab === 0 && (
              <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="600">
                      Participants ({participantDetails.length})
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PersonAddIcon />}
                      onClick={() => setAddPersonDialog(true)}
                      sx={{ borderRadius: 2 }}
                    >
                      Add Person
                    </Button>
                  </Box>

                  <List>
                    {participantDetails.map((participant, index) => (
                      <ListItem
                        key={participant.id || index}
                        sx={{
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          borderRadius: 2,
                          mb: 1,
                          bgcolor: participant.isCurrentUser ? alpha(theme.palette.primary.main, 0.05) : 'transparent'
                        }}
                      >
                        <ListItemIcon>
                          <Badge
                            badgeContent={participant.hasPaid ? <CheckIcon sx={{ fontSize: 12 }} /> : ''}
                            color="success"
                          >
                            <Avatar
                              sx={{
                                bgcolor: participant.hasPaid ? theme.palette.success.main : theme.palette.grey[400],
                                color: 'white'
                              }}
                            >
                              {participant.name.charAt(0).toUpperCase()}
                            </Avatar>
                          </Badge>
                        </ListItemIcon>
                        
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" fontWeight={participant.isCurrentUser ? 600 : 400}>
                                {participant.name}
                                {participant.isCurrentUser && ' (You)'}
                              </Typography>
                              {participant.isCreator && (
                                <Chip label="Creator" size="small" color="primary" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Share: {formatCurrency(participant.share)}
                              </Typography>
                              {bill.splitMethod === 'itemized' && (
                                <Typography variant="body2" color="text.secondary">
                                  Paid: {formatCurrency(participant.paidAmount || 0)}
                                </Typography>
                              )}
                              {participant.email && (
                                <Typography variant="caption" display="block">
                                  <EmailIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                  {participant.email}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        
                        <Box sx={{ textAlign: 'right', mr: 2 }}>
                          {participant.balance > 0 ? (
                            <Typography variant="body1" color="success.main" fontWeight="600">
                              +{formatCurrency(participant.balance)}
                            </Typography>
                          ) : participant.balance < 0 ? (
                            <Typography variant="body1" color="error.main" fontWeight="600">
                              {formatCurrency(participant.balance)}
                            </Typography>
                          ) : (
                            <Typography variant="body1" color="text.secondary">
                              Even
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {participant.hasPaid ? 'Paid' : 'Pending'}
                          </Typography>
                        </Box>

                        <ListItemSecondaryAction>
                          <Stack direction="row" spacing={0.5}>
                            {!participant.hasPaid && participant.owes > 0 && (
                              <Tooltip title="Record Payment">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setSelectedParticipant(participant);
                                    setPaymentAmount(participant.owes.toString());
                                    setPaymentDialog(true);
                                  }}
                                >
                                  <PaymentIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            <Tooltip title="Send Reminder">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => {
                                  setSelectedParticipant(participant);
                                  setReminderDialog(true);
                                }}
                              >
                                <NotificationIcon />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Contact">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (participant.email) {
                                    window.location.href = `mailto:${participant.email}`;
                                  }
                                }}
                              >
                                <MessageIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}

            {activeTab === 1 && (
              <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    Expenses
                  </Typography>

                  {bill.splitMethod === 'itemized' && bill.expenses && bill.expenses.length > 0 ? (
                    <List>
                      {bill.expenses.map((expense, index) => (
                        <ListItem
                          key={expense.id || index}
                          sx={{
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            borderRadius: 2,
                            mb: 1
                          }}
                        >
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                              <ReceiptIcon />
                            </Avatar>
                          </ListItemIcon>
                          
                          <ListItemText
                            primary={expense.description}
                            secondary={
                              <Box>
                                <Typography variant="caption" display="block">
                                  Paid by: {participantDetails[expense.paidBy]?.name || 'Unknown'}
                                </Typography>
                                {expense.sharedBy && (
                                  <Typography variant="caption" display="block">
                                    Shared by: {expense.sharedBy.map(i => participantDetails[i]?.name).filter(Boolean).join(', ')}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                          
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h6" fontWeight="600">
                              {formatCurrency(expense.amount)}
                            </Typography>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography variant="body1" color="text.secondary">
                        {bill.splitMethod === 'itemized' 
                          ? 'No individual expenses recorded'
                          : `Split equally using ${bill.splitMethod} method`
                        }
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total amount: {formatCurrency(bill.totalAmount)}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 2 && (
              <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    Activity Timeline
                  </Typography>

                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                          <GroupIcon />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary="Bill Created"
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(bill.createdAt || bill.date).toLocaleString()}
                            </Typography>
                            <Typography variant="body2">
                              Created by {participantDetails.find(p => p.isCreator)?.name || 'Unknown'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>

                    {/* Add more activity items based on bill history */}
                    {participantDetails.filter(p => p.hasPaid).map((participant, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                            <PaymentIcon />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary="Payment Received"
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {participant.paymentDate ? new Date(participant.paymentDate).toLocaleString() : 'Date not available'}
                              </Typography>
                              <Typography variant="body2">
                                {participant.name} paid {formatCurrency(participant.share)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}

            {activeTab === 3 && (
              <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    Summary & Statistics
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                        <Typography variant="h4" color="primary.main" fontWeight="700">
                          {formatCurrency(bill.totalAmount)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Amount
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                        <Typography variant="h4" color="success.main" fontWeight="700">
                          {formatCurrency(totals.totalPaid)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Paid
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                        <Typography variant="h4" color="info.main" fontWeight="700">
                          {participantDetails.filter(p => p.hasPaid).length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Participants Paid
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                        <Typography variant="h4" color="warning.main" fontWeight="700">
                          {formatCurrency(bill.totalAmount - totals.totalPaid)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Outstanding
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />

                  {/* Payment Progress */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2">
                        Payment Progress
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {Math.round((totals.totalPaid / bill.totalAmount) * 100)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((totals.totalPaid / bill.totalAmount) * 100, 100)}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    />
                  </Box>

                  {/* Split Method Details */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1" fontWeight="600">
                        Split Method: {bill.splitMethod.charAt(0).toUpperCase() + bill.splitMethod.slice(1)}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {bill.splitMethod === 'equal' && 'The total amount is split equally among all participants.'}
                        {bill.splitMethod === 'percentage' && 'Each participant pays a specific percentage of the total.'}
                        {bill.splitMethod === 'custom' && 'Each participant pays a custom amount.'}
                        {bill.splitMethod === 'itemized' && 'Participants pay for specific items they shared.'}
                      </Typography>
                      
                      <Typography variant="subtitle2" gutterBottom>
                        Individual Shares:
                      </Typography>
                      {participantDetails.map((participant, index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="body2">
                            {participant.name}
                          </Typography>
                          <Typography variant="body2" fontWeight="600">
                            {formatCurrency(participant.share)}
                          </Typography>
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {/* Quick Actions */}
              <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    Quick Actions
                  </Typography>
                  
                  <Stack spacing={2}>
                    {totals.userOwes > 0 && (
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<PaymentIcon />}
                        onClick={() => {
                          const currentUser = participantDetails.find(p => p.isCurrentUser);
                          setSelectedParticipant(currentUser);
                          setPaymentAmount(totals.userOwes.toString());
                          setPaymentDialog(true);
                        }}
                        sx={{ borderRadius: 2 }}
                      >
                        Pay {formatCurrency(totals.userOwes)}
                      </Button>
                    )}
                    
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<ShareIcon />}
                      onClick={() => setShareDialog(true)}
                      sx={{ borderRadius: 2 }}
                    >
                      Share Bill
                    </Button>
                    
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<QrCodeIcon />}
                      onClick={() => setQrCodeDialog(true)}
                      sx={{ borderRadius: 2 }}
                    >
                      Show QR Code
                    </Button>
                    
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<DownloadIcon />}
                      onClick={(e) => setReportMenuAnchor(e.currentTarget)}
                      sx={{ borderRadius: 2 }}
                    >
                      Settlement Report
                    </Button>
                    <Menu
                      anchorEl={reportMenuAnchor}
                      open={Boolean(reportMenuAnchor)}
                      onClose={() => setReportMenuAnchor(null)}
                      PaperProps={{ sx: { borderRadius: 2, minWidth: 220 } }}
                    >
                      <MenuItem onClick={handleDownloadReportPDF}>
                        <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Download PDF" secondary="Save to device" />
                      </MenuItem>
                      <MenuItem onClick={handleCopyReportLink}>
                        <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Copy Share Link" secondary="Shareable web report" />
                      </MenuItem>
                    </Menu>
                  </Stack>
                </CardContent>
              </Card>

              {/* Receipt Image */}
              {bill.receiptImage && (
                <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      Receipt
                    </Typography>
                    <Box
                      component="img"
                      src={`/api/receipts/${bill._id}/image`}
                      sx={{
                        width: '100%',
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Settings */}
              <Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    Settings
                  </Typography>
                  
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Allow partial payments"
                        secondary={bill.settings?.allowPartialPayments ? "Enabled" : "Disabled"}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Payment reminders"
                        secondary={bill.settings?.sendReminders ? "Enabled" : "Disabled"}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Public visibility"
                        secondary={bill.settings?.makePublic ? "Public" : "Private"}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>

        {/* Floating Action Buttons */}
        <SpeedDial
          ariaLabel="Bill actions"
          sx={{ position: 'fixed', bottom: 24, right: 24 }}
          icon={<SpeedDialIcon />}
        >
          <SpeedDialAction
            icon={<PaymentIcon />}
            tooltipTitle="Record Payment"
            onClick={() => setPaymentDialog(true)}
          />
          <SpeedDialAction
            icon={<ShareIcon />}
            tooltipTitle="Share"
            onClick={() => setShareDialog(true)}
          />
          <SpeedDialAction
            icon={<EditIcon />}
            tooltipTitle="Edit"
            onClick={() => setEditDialog(true)}
          />
          <SpeedDialAction
            icon={<PersonAddIcon />}
            tooltipTitle="Add Person"
            onClick={() => setAddPersonDialog(true)}
          />
        </SpeedDial>

        {/* Dialogs */}
        {/* Payment Dialog */}
        <Dialog
          open={paymentDialog}
          onClose={() => setPaymentDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Record Payment</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Amount"
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />
              <TextField
                select
                label="Payment Method"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                fullWidth
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="paypal">PayPal</MenuItem>
                <MenuItem value="venmo">Venmo</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handlePayment} variant="contained">
              Record Payment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Share Dialog */}
        <Dialog
          open={shareDialog}
          onClose={() => setShareDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Share Bill</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => handleShare('copy')}
                fullWidth
              >
                Copy Link
              </Button>
              <Button
                variant="outlined"
                startIcon={<WhatsAppIcon />}
                onClick={() => handleShare('whatsapp')}
                fullWidth
                sx={{ color: '#25D366', borderColor: '#25D366' }}
              >
                Share via WhatsApp
              </Button>
              <Button
                variant="outlined"
                startIcon={<TelegramIcon />}
                onClick={() => handleShare('telegram')}
                fullWidth
                sx={{ color: '#0088cc', borderColor: '#0088cc' }}
              >
                Share via Telegram
              </Button>
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={() => handleShare('email')}
                fullWidth
              >
                Share via Email
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShareDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem onClick={() => { setEditDialog(true); setMenuAnchor(null); }}>
            <ListItemIcon><EditIcon /></ListItemIcon>
            <ListItemText>Edit Bill</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { setShareDialog(true); setMenuAnchor(null); }}>
            <ListItemIcon><ShareIcon /></ListItemIcon>
            <ListItemText>Share</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => setMenuAnchor(null)}>
            <ListItemIcon><DownloadIcon /></ListItemIcon>
            <ListItemText>Export PDF</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => setMenuAnchor(null)}>
            <ListItemIcon><PrintIcon /></ListItemIcon>
            <ListItemText>Print</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => setMenuAnchor(null)} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteIcon sx={{ color: 'error.main' }} /></ListItemIcon>
            <ListItemText>Delete Bill</ListItemText>
          </MenuItem>
        </Menu>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    </Fade>
  );
};

export default EnhancedSplitBillDetails;
